import { v7 as uuidv7 } from 'uuid'
import { config } from '@/config'
import { logger } from '@/lib'
import { CookieKeys, CookieService } from '@/services/cookie'
import { AnalyticsSessionService } from '@/services/analyticsSession'
import {
  AnalyticsEventPayload,
  AnalyticsEventType,
  QueuedAnalyticsEvent,
  StandaloneContext,
} from '@/interfaces/analytics'

const FLUSH_BATCH_SIZE = 10
const FLUSH_TIMER_MS = 5000
const SERVER_BATCH_LIMIT = 20

// Best-effort analytics transport (CW2-648 §7.1). Never throws into UI code —
// every public surface is wrapped. Uses its own raw fetch with keepalive
// (NOT TaskService: its unconditional abort-timeout contradicts a beacon that
// must outlive the page). Ambiguous outcomes retry ONCE with the same uids,
// then drop; unauthenticated events are dropped silently.
export class AnalyticsService {
  private static queue: QueuedAnalyticsEvent[] = []

  private static flushTimer: ReturnType<typeof setTimeout> | null = null

  public static track(type: AnalyticsEventType, payload?: AnalyticsEventPayload): void {
    try {
      if (typeof window === 'undefined') return
      if (!this.getToken()) return

      let uid: string
      try {
        uid = uuidv7()
      } catch {
        // an event whose uid cannot be generated is dropped, never thrown
        return
      }

      this.queue.push({
        uid,
        type,
        payload: this.withStandalone(payload),
        occurredAt: new Date().toISOString(),
      })

      if (this.queue.length >= FLUSH_BATCH_SIZE) {
        this.flush()
      } else {
        this.scheduleFlush()
      }
    } catch (error) {
      logger.error(error)
    }
  }

  // Also called by AuthProvider BEFORE the token cookie changes: registrant_id
  // is stamped server-side from the token at receive time, so events queued
  // under one registrant must never be flushed under another's token.
  public static flush(): void {
    try {
      this.clearFlushTimer()
      if (!this.queue.length) return

      const token = this.getToken()
      if (!token) {
        this.queue = []
        return
      }

      while (this.queue.length) {
        const events = this.queue.splice(0, SERVER_BATCH_LIMIT)
        void this.sendBatch(events, token)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  private static async sendBatch(events: QueuedAnalyticsEvent[], token: string, isRetry = false): Promise<void> {
    try {
      const response = await fetch(`${config.api.baseUrl}/event/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token,
        },
        body: JSON.stringify({ events }),
        // the flush must be able to outlive the page (pagehide/visibilitychange)
        keepalive: true,
      })

      if (response.status === 503 && !isRetry) {
        await this.sendBatch(events, token, true)
      }
    } catch (error) {
      // network error — the outcome is ambiguous, retry once with the SAME uids
      // (idempotent server-side), then drop
      if (!isRetry) {
        await this.sendBatch(events, token, true)
      } else {
        logger.error(error)
      }
    }
  }

  // Every event carries isStandalone (§4.1) so installed-app vs browser can be split for any
  // action, not only app_open. An explicit value from the caller (app_open's async resolution)
  // is kept; otherwise it is resolved synchronously at fire time.
  private static withStandalone(payload?: AnalyticsEventPayload): AnalyticsEventPayload | StandaloneContext {
    const base: StandaloneContext = payload ?? {}
    if (typeof base.isStandalone === 'boolean') return base
    return { ...base, isStandalone: AnalyticsSessionService.resolveIsStandaloneSync() }
  }

  private static scheduleFlush(): void {
    if (this.flushTimer !== null) return
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      this.flush()
    }, FLUSH_TIMER_MS)
  }

  private static clearFlushTimer(): void {
    if (this.flushTimer === null) return
    clearTimeout(this.flushTimer)
    this.flushTimer = null
  }

  private static getToken(): string | null {
    try {
      const token = CookieService.getItem(CookieKeys.RegistrantToken)
      return token ? String(token) : null
    } catch {
      return null
    }
  }

  public static resetForTesting(): void {
    this.queue = []
    this.clearFlushTimer()
  }

  public static get queueSizeForTesting(): number {
    return this.queue.length
  }
}
